import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Mail, Lock, Quote } from 'lucide-react';
import { toast } from 'sonner';
import { getRandomQuote } from '../data/motivationalQuotes';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(getRandomQuote());
  const { login } = useAuth();
  const navigate = useNavigate();

  // Rotate quotes every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setQuote(getRandomQuote());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center bg-cover bg-right bg-fixed relative"
      style={{ backgroundImage: `url('https://images.pexels.com/photos/17796162/pexels-photo-17796162.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
      
      {/* Left side - Login content */}
      <div className="relative z-10 w-full lg:w-1/2 flex flex-col items-center justify-center py-8 px-4">
        {/* Motivational Quote Banner */}
        <div className="w-full max-w-md mb-6">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/20 shadow-lg">
            <div className="flex items-start gap-3">
              <Quote className="h-6 w-6 text-[#E31837] flex-shrink-0 mt-1" />
              <div>
                <p className="text-white text-base font-medium italic leading-relaxed">{quote.text}</p>
                <p className="text-white/70 text-sm mt-2">— {quote.author}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Login Card */}
        <Card data-testid="login-card" className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/peacock-logo.png" alt="VRMX" className="h-16 w-16 object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-[#333333] mb-2">VRMX</h1>
          <p className="text-gray-600">Business Management Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Link 
              to="/forgot-password" 
              className="text-sm text-[#E31837] hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            data-testid="login-submit-button"
            className="w-full bg-[#E31837] hover:bg-[#C4122C] text-white"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don&apos;t have an account?{' '}
            <Link 
              to="/register" 
              className="text-[#E31837] hover:underline font-medium"
              data-testid="register-link"
            >
              Sign up
            </Link>
          </p>
        </div>
      </Card>
      </div>
    </div>
  );
};
